import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Spinner, SystemPermissionRequired } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { Button, IconButton } from '@mui/material';
import MoreIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/More.js';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import { FormAuditLogTable } from 'src/components/organisms/FormAuditLogTable';
import { FormPreview } from 'src/components/organisms/FormPreview';
import {
  useFormManagementQuestionnaireByIdQuery,
  useFormManagementQuestionsQuery,
  useGetOrganisationCategoriesQuery,
  type CategoryType,
} from 'src/graphql';
import { usePreventOutsideClickOnToast } from 'src/hooks/usePreventOutsideClickOnToast';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { stripHtml } from 'src/utils/stripHtml';
import { DateFormats, formatDate } from 'src/utils/time';
import { transformSpeakUpCategories } from '../../FormCreation/components/Configuration/helpers/categoryDataTransformers';
import { isSameCategoryName } from '../../FormCreation/components/Configuration/helpers/isSameCategoryName';
import { QuestionTypesProvider } from '../../FormCreation/contexts/QuestionTypesContext';
import type { DuplicateFromState } from '../../FormCreation/FormCreation';
import { getSlugFromFormType } from '../../FormCreation/helpers/formTypeMapper';
import { ArchiveFormConfirmModal } from '../ArchiveFormConfirmModal';
import { UnpublishFormConfirmModal } from '../UnpublishFormConfirmModal';
import { getFormManagementUrlWithTab } from '../utils/getFormManagementUrl';
import { CategoryDetailsStatusBadge } from '../CategoryDetailsPage/CategoryDetailsStatusBadge.styles';
import type { CategoryDetailsFormRow } from '../CategoryDetailsPage/types';
import { DuplicateFormModal } from '../DuplicateFormModal';
import {
  getCreatedByDisplay,
  getQuestionnaireLanguageNames,
  resolveQuestionnaireLanguageDisplayLabel,
} from '../questionnaireListDisplay';
import { statusToBadgeVariant } from '../formManagementStatusBadge';
import { FormStatusTab, FormType } from '../types';
import {
  FormDetailActions,
  FormDetailBackAndTitle,
  FormDetailBackButton,
  FormDetailBackButtonWrapper,
  FormDetailContentContainer,
  FormDetailEmptyWrapper,
  FormDetailHeaderRow,
  FormDetailPageContainer,
  FormDetailTitle,
  FormDetailTitleWithBadge,
  StyledFormDetailButton,
} from './FormDetailPage.styles';
import { FormDetailActionsMenu } from './FormDetailActionsMenu';
import {
  mapQuestionnaireStatusToFormStatusTab,
  toFormDetailDisplayStatus,
} from './formDetailQuestionnaireStatus';
import { formDetailQuestionsToQuestionData } from './formDetailQuestionsToQuestionData';
import { FormInformationSection } from './FormInformationSection';
import { getFormDetailHeaderActionFlags } from './getFormDetailHeaderActionFlags';
import { useFormDetailStatusActions } from './useFormDetailStatusActions';

export const FormDetailPage = () => {
  usePreventOutsideClickOnToast();
  const history = useHistory();
  const location = useLocation();
  const { type, formId } = useParams<{
    type: string;
    formId: string;
  }>();
  const returnTo = (location.state as { returnTo?: string } | undefined)
    ?.returnTo;
  const [selectedPreviewLanguage, setSelectedPreviewLanguage] = useState<
    string | null
  >(null);
  const [formToDuplicate, setFormToDuplicate] = useState<{
    id: string;
    title: string;
    languages: Array<{ code: string; name: string }>;
    sourceQuestionnaireStatus: FormStatusTab;
  } | null>(null);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const { data: questionnaireData, isLoading: isQuestionnaireLoading } =
    useFormManagementQuestionnaireByIdQuery(
      { id: formId! },
      { enabled: !!formId },
    );

  const { data: questionsData, isLoading: isQuestionsLoading } =
    useFormManagementQuestionsQuery(
      { questionnaireId: formId! },
      { enabled: !!formId },
    );

  const questionnaire = questionnaireData?.formManagementQuestionnaireById;

  const sourceSection = useMemo(() => {
    const catKey = questionnaire?.formCategory?.categoryKey;
    if (catKey === 'speak_up') return 'SPEAK_UP' as const;
    return undefined;
  }, [questionnaire]);

  const formTypeFromSection = useMemo((): FormType => {
    if (sourceSection === 'SPEAK_UP') {
      return FormType.SPEAK_UP;
    }
    if (sourceSection === undefined && type === 'speak-up') {
      return FormType.SPEAK_UP;
    }
    return FormType.CONFLICT_OF_INTEREST;
  }, [sourceSection, type]);

  const {
    data: organisationCategoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetOrganisationCategoriesQuery(
    { isCOI: formTypeFromSection === FormType.CONFLICT_OF_INTEREST },
    { enabled: !!questionnaire },
  );

  const categoryMatchFields = useMemo(() => {
    const fallbackName = questionnaire?.category?.trim() ?? '';
    const categoryId = questionnaire?.categoryId;
    const list =
      organisationCategoriesData?.getOrganisationCategories ?? undefined;

    if (isCategoriesError) {
      return {
        intakeChannelsStatus: 'error' as const,
        categoryName: fallbackName,
        categoryDescription: '',
        displayInApp: false,
        allowGoTogether: false,
        isDeterminedCategory: false,
        isReportedCategory: false,
      };
    }

    if (isCategoriesLoading && !list?.length) {
      return {
        intakeChannelsStatus: 'loading' as const,
        categoryName: fallbackName,
        categoryDescription: '',
        displayInApp: false,
        allowGoTogether: false,
        isDeterminedCategory: false,
        isReportedCategory: false,
      };
    }

    if (!list?.length) {
      return {
        intakeChannelsStatus: 'ready' as const,
        categoryName: fallbackName,
        categoryDescription: '',
        displayInApp: false,
        allowGoTogether: false,
        isDeterminedCategory: false,
        isReportedCategory: false,
      };
    }

    const transformed = transformSpeakUpCategories(list as CategoryType[]);
    const match =
      categoryId != null
        ? transformed.find((c) => c.id === categoryId)
        : fallbackName
          ? transformed.find((c) => isSameCategoryName(c.name, fallbackName))
          : undefined;
    return {
      intakeChannelsStatus: 'ready' as const,
      categoryName: match?.name ?? fallbackName,
      displayInApp: match?.displayInApp === true,
      allowGoTogether: match?.allowGoTogether === true,
      categoryDescription: match?.description ?? '',
      isDeterminedCategory: match?.isDeterminedCategory === true,
      isReportedCategory: match?.isReportedCategory === true,
    };
  }, [
    questionnaire?.category,
    questionnaire?.categoryId,
    organisationCategoriesData?.getOrganisationCategories,
    isCategoriesLoading,
    isCategoriesError,
  ]);

  const languageNames = useMemo(
    () => (questionnaire ? getQuestionnaireLanguageNames(questionnaire) : []),
    [questionnaire],
  );

  const formDetail = useMemo(() => {
    if (!questionnaire) return null;
    return {
      metadata: {
        formId: questionnaire.id ?? '',
        formName: questionnaire.publicTitle ?? questionnaire.internalName ?? '',
        description:
          questionnaire.introduction ?? questionnaire.description ?? '',
        status: toFormDetailDisplayStatus(questionnaire.status),
        createdDate: formatDate(
          questionnaire.createdAt,
          DateFormats.YYYY_MM_DD_TIME_SHORT,
          false,
        ),
        lastUpdatedDate: formatDate(
          questionnaire.updatedAt,
          DateFormats.YYYY_MM_DD_TIME_SHORT,
          false,
        ),
        country:
          questionnaire.countries?.map((c) => c.countryName).join(', ') ?? '',
        countries: questionnaire.countries?.map((c) => c.countryName) ?? [],
        languageVariants: languageNames,
        createdBy: getCreatedByDisplay(questionnaire.createdBy),
        category: categoryMatchFields.categoryName,
        categoryDescription: categoryMatchFields.categoryDescription,
        displayInApp: categoryMatchFields.displayInApp,
        allowGoTogether: categoryMatchFields.allowGoTogether,
        intakeChannelsStatus: categoryMatchFields.intakeChannelsStatus,
        isDeterminedCategory: categoryMatchFields.isDeterminedCategory,
        isReportedCategory: categoryMatchFields.isReportedCategory,
        formType: (sourceSection === 'SPEAK_UP' ? 'SPEAK_UP' : 'COI') as
          | 'SPEAK_UP'
          | 'COI',
        autoReview: questionnaire.autoReviewEnabled ?? false,
      },
      questionsByLanguage: {} as Record<string, never>,
    };
  }, [questionnaire, languageNames, categoryMatchFields, sourceSection]);

  const previewQuestions = useMemo(() => {
    const raw = questionsData?.formManagementQuestions?.questions ?? [];
    return formDetailQuestionsToQuestionData(raw);
  }, [questionsData]);

  const formRow = useMemo((): CategoryDetailsFormRow | null => {
    if (!questionnaire) return null;
    return {
      id: questionnaire.id ?? '',
      title: questionnaire.publicTitle ?? questionnaire.internalName ?? '',
      category: categoryMatchFields.categoryName,
      country: '',
      languages: languageNames,
      questionsCount: questionsData?.formManagementQuestions?.count ?? 0,
      createdBy: getCreatedByDisplay(questionnaire.createdBy),
      createdAt: questionnaire.createdAt ?? '',
      lastUpdated: questionnaire.updatedAt ?? '',
      status: mapQuestionnaireStatusToFormStatusTab(questionnaire.status),
    };
  }, [questionnaire, languageNames, questionsData, categoryMatchFields]);

  const previewLanguageOptions = useMemo(() => {
    const langs = questionnaire?.languages ?? [];
    return langs
      .map((l) => {
        const code = l?.code?.trim() ?? '';
        if (!code) return null;
        const label = resolveQuestionnaireLanguageDisplayLabel(code, l?.name);
        return { value: code, label: label || code };
      })
      .filter((opt): opt is { value: string; label: string } => opt != null)
      .sort(
        (a, b) =>
          a.label.localeCompare(b.label) || a.value.localeCompare(b.value),
      );
  }, [questionnaire?.languages]);

  const previewLanguage = useMemo(() => {
    if (previewLanguageOptions.length === 0) return 'en-GB';
    if (
      selectedPreviewLanguage &&
      previewLanguageOptions.some((o) => o.value === selectedPreviewLanguage)
    ) {
      return selectedPreviewLanguage;
    }
    const englishMatch = previewLanguageOptions.find(
      (o) => o.value === 'en-GB' || o.label.trim().toLowerCase() === 'english',
    );
    return englishMatch?.value ?? previewLanguageOptions[0].value;
  }, [previewLanguageOptions, selectedPreviewLanguage]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const navigateToFormList = useCallback(() => {
    history.push(getFormManagementUrlWithTab(formTypeFromSection));
  }, [history, formTypeFromSection]);

  const handleBack = useCallback(() => {
    if (returnTo) {
      history.push(returnTo);
      return;
    }
    navigateToFormList();
  }, [history, returnTo, navigateToFormList]);

  const handleEditForm = useCallback(() => {
    handleMenuClose();
    if (formId && questionnaire) {
      const slug = getSlugFromFormType(formTypeFromSection);
      const fallbackReturnTo = getFormManagementUrlWithTab(formTypeFromSection);
      history.push(`/form-creation/${slug}/${formId}`, {
        returnTo: returnTo ?? fallbackReturnTo,
      });
    }
  }, [
    handleMenuClose,
    history,
    formId,
    questionnaire,
    formTypeFromSection,
    returnTo,
  ]);

  const handleAuditLog = useCallback(() => {
    handleMenuClose();
    setAuditLogOpen(true);
  }, [handleMenuClose]);

  const {
    formToArchive,
    setFormToArchive,
    handleArchive,
    handleArchiveConfirm,
    formToUnpublish,
    setFormToUnpublish,
    handleUnpublish,
    handleUnpublishConfirm,
    isStatusMutationLoading,
  } = useFormDetailStatusActions({
    formRow,
    onMenuClose: handleMenuClose,
    onArchiveSuccess: navigateToFormList,
  });

  const handleDuplicate = useCallback(() => {
    if (formDetail && formId && formRow && questionnaire) {
      setFormToDuplicate({
        id: formId,
        title: formDetail.metadata.formName,
        languages: (questionnaire.languages ?? [])
          .filter((l): l is NonNullable<typeof l> => Boolean(l?.code))
          .map((l) => ({
            code: l.code!,
            name: l.name ?? l.code!,
          })),
        sourceQuestionnaireStatus: formRow.status,
      });
    }
  }, [formDetail, formId, formRow, questionnaire]);

  const handleDuplicateConfirm = useCallback(
    (mode: 'all-languages' | 'default-only', newTitle: string) => {
      if (!formToDuplicate || !formId || !questionnaire) return;

      const slug = getSlugFromFormType(formTypeFromSection);
      const formDetailUrl = `/form-management/${slug}/form/${formId}`;

      const sourceLanguages = (formToDuplicate.languages ?? [])
        .filter((l): l is NonNullable<typeof l> => Boolean(l.code))
        .map((l) => ({
          label: l.name || l.code,
          value: l.code,
          isDefault: false,
        }));

      const duplicateFrom: DuplicateFromState = {
        sourceTitle: formToDuplicate.title,
        displayName: newTitle,
        sourceQuestionnaireId: formToDuplicate.id,
        sourceQuestionnaireStatus: formToDuplicate.sourceQuestionnaireStatus,
        sourceLanguages: mode === 'all-languages' ? sourceLanguages : undefined,
      };
      setFormToDuplicate(null);
      const fallbackReturnTo = returnTo ?? formDetailUrl;
      history.push(`/form-creation/${slug}`, {
        returnTo: fallbackReturnTo,
        duplicateFrom,
      });
    },
    [
      formToDuplicate,
      formTypeFromSection,
      questionnaire,
      formId,
      history,
      returnTo,
    ],
  );

  const headerActionFlags = useMemo(
    () =>
      getFormDetailHeaderActionFlags(questionnaire?.status, {
        isCoiForm: formTypeFromSection === FormType.CONFLICT_OF_INTEREST,
      }),
    [questionnaire?.status, formTypeFromSection],
  );

  if (isQuestionnaireLoading || isQuestionsLoading) {
    return (
      <FormDetailPageContainer
        data-testid="form-detail-page"
        style={{ justifyContent: 'center', alignItems: 'center' }}
      >
        <Spinner variant="secondary" size="large" />
      </FormDetailPageContainer>
    );
  }

  if (!formId || !formDetail) {
    return (
      <FormDetailPageContainer data-testid="form-detail-page">
        <FormDetailContentContainer>
          <FormDetailEmptyWrapper data-testid="form-detail-not-found">
            <EmptyState
              title="Form not found"
              description="The form could not be found."
              cta={
                <Button
                  variant="contained"
                  onClick={navigateToFormList}
                  data-testid="form-detail-not-found-button"
                >
                  Back to Forms
                </Button>
              }
            />
          </FormDetailEmptyWrapper>
        </FormDetailContentContainer>
      </FormDetailPageContainer>
    );
  }

  const { metadata } = formDetail;
  const formStatusTab = mapQuestionnaireStatusToFormStatusTab(
    questionnaire?.status,
  );

  return (
    <QuestionTypesProvider
      formCategoryId={questionnaire?.formCategory?.id ?? undefined}
    >
      <FormDetailPageContainer data-testid="form-detail-page">
        <FormDetailContentContainer>
          <FormDetailHeaderRow data-testid="form-detail-header">
            <FormDetailBackAndTitle>
              <FormDetailBackButtonWrapper>
                <FormDetailBackButton
                  variant="tertiary"
                  noBorder
                  size="medium"
                  fontSize="24px"
                  iconName="uil uil-arrow-left"
                  onClick={handleBack}
                  aria-label="Back"
                  data-testid="form-detail-back-button"
                />
              </FormDetailBackButtonWrapper>
              <FormDetailTitleWithBadge>
                <FormDetailTitle
                  data-testid="form-detail-title"
                  title={metadata.formName}
                >
                  {metadata.formName}
                </FormDetailTitle>
                <CategoryDetailsStatusBadge
                  variant={statusToBadgeVariant(formStatusTab)}
                  text={metadata.status}
                  $status={formStatusTab}
                  data-testid="form-detail-status-badge"
                />
              </FormDetailTitleWithBadge>
            </FormDetailBackAndTitle>
            <SystemPermissionRequired permission={SYSTEM_PERMISSIONS.NAV_ADMIN}>
              <FormDetailActions data-testid="form-detail-actions-group">
                <IconButton
                  onClick={handleMenuOpen}
                  aria-label="More actions"
                  aria-expanded={isMenuOpen}
                  aria-haspopup="menu"
                  aria-controls={
                    isMenuOpen ? 'form-detail-actions-menu' : undefined
                  }
                  data-testid="form-detail-more-actions-button"
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '12px',
                  }}
                >
                  <MoreIcon aria-hidden />
                </IconButton>
                {headerActionFlags.showDuplicate && (
                  <StyledFormDetailButton
                    variant="primary"
                    size="medium"
                    onClick={handleDuplicate}
                    data-testid="form-detail-duplicate-button"
                    aria-label="Duplicate form"
                  >
                    <StyledIconInButton
                      size="medium"
                      position="left"
                      className="uil uil-copy"
                    />
                    Duplicate form
                  </StyledFormDetailButton>
                )}
              </FormDetailActions>
            </SystemPermissionRequired>
          </FormDetailHeaderRow>

          <FormDetailActionsMenu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            onEdit={handleEditForm}
            onArchive={handleArchive}
            onAuditLog={handleAuditLog}
            onUnpublish={handleUnpublish}
            showEdit={headerActionFlags.showEdit}
            showArchive={headerActionFlags.showArchive}
            showAuditLog={headerActionFlags.showAuditLog}
            showUnpublish={headerActionFlags.showUnpublish}
            archiveDisabled={headerActionFlags.archiveDisabled}
          />

          <SystemPermissionRequired permission={SYSTEM_PERMISSIONS.NAV_ADMIN}>
            <FormInformationSection metadata={metadata} />
            <FormPreview
              variant="inline"
              inlinePlacement="formManagement"
              title={metadata.formName}
              description={(() => {
                if (formTypeFromSection !== FormType.SPEAK_UP) {
                  return metadata.description;
                }
                return stripHtml(metadata.categoryDescription ?? '').trim()
                  ? metadata.categoryDescription
                  : metadata.description;
              })()}
              languageOptions={previewLanguageOptions}
              questions={previewQuestions}
              selectedLanguage={previewLanguage}
              onLanguageChange={setSelectedPreviewLanguage}
              data-testid="form-detail-questions-preview"
            />
          </SystemPermissionRequired>
        </FormDetailContentContainer>
        <ArchiveFormConfirmModal
          open={!!formToArchive}
          setOpen={(open) => !open && setFormToArchive(null)}
          onConfirm={handleArchiveConfirm}
          onCancel={() => setFormToArchive(null)}
          data-testid="form-detail-archive-confirm-modal"
        />
        <UnpublishFormConfirmModal
          open={!!formToUnpublish}
          setOpen={(open) => !open && setFormToUnpublish(null)}
          onConfirm={handleUnpublishConfirm}
          onCancel={() => setFormToUnpublish(null)}
          isLoading={isStatusMutationLoading}
          formTitle={formToUnpublish?.title}
          data-testid="form-detail-unpublish-confirm-modal"
        />
        <DuplicateFormModal
          open={formToDuplicate != null}
          setOpen={(open) => !open && setFormToDuplicate(null)}
          onConfirm={handleDuplicateConfirm}
          isLoading={false}
          formType={formTypeFromSection}
          data-testid="form-detail-duplicate-modal"
        />
        <FormAuditLogTable
          isOpen={auditLogOpen}
          setIsOpen={setAuditLogOpen}
          questionnaireId={formId}
          questionnaireTitle={
            questionnaire?.publicTitle ?? questionnaire?.internalName ?? ''
          }
        />
      </FormDetailPageContainer>
    </QuestionTypesProvider>
  );
};
